import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
// Import Components
import BonusInfoModalBody from './BonusInfoModalBody';
// Import UI Components
import { Modal, Button } from '@geneui/components';
// Import Constants
import { ModalsActions, l } from 'constants/common';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Services
import { BonusHttpService } from 'services/http';
//Import SCSS
import 'assets/scss/segmentModal.scss';
// Import Version History
import {
    useVersionHistoryContext,
    VersionHistoryIconWithPopover,
    VersionHistoryProviderHOC,
    VersionHistorySelectedVersionLabel,
} from 'components/VersionHistory';
// Import Enums
import { ObjectTypeEnum } from 'enums';

interface Props {
    bonusId?: string;
    isVisible?: boolean;
    handleClose?: (action: string) => void;
}

const BonusModal: React.FC<Props> = ({ handleClose, isVisible, bonusId }) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [bonusData, setBonusData] = useState<Nullable<AnyObject>>(null);

    const { doGetRequest } = useRequest();

    const { pointInTime } = useVersionHistoryContext();

    const { getBonusById } = useCancelableRequests(
        { getBonusById: BonusHttpService.getBonusById },
        { takeLatest: ['getBonusById'] },
    );

    useEffect(() => {
        setBonusData(null);
        setIsLoading(true);
        if (!isNil(bonusId)) {
            doGetRequest(getBonusById.request, {
                queryString: { bonusId, pointInTime },
                successCallback: (data) => {
                    setIsLoading(false);
                    setBonusData(data);
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bonusId, pointInTime]);

    return (
        <Modal
            withPortal={true}
            closeOnClickOutside={true}
            background="dark-background"
            position="center"
            visible={isVisible}
            closable={false}
            onCancel={() => handleClose?.(ModalsActions.CLOSE)}
            className="crm-object-list-modal"
            title={
                <div className="crm-object-list-modal-head">
                    <span className="crm-object-list-modal-h-text">{t(l.BonusInfo)}</span>
                    <div className="crm-object-list-modal-h-icons">
                        <VersionHistorySelectedVersionLabel
                            objectId={+(bonusId ?? '')}
                            objectTypeId={ObjectTypeEnum.Bonus}
                        />
                        <VersionHistoryIconWithPopover
                            objectId={+(bonusId ?? '')}
                            objectTypeId={ObjectTypeEnum.Bonus}
                        />
                    </div>
                </div>
            }
            footer={
                <Button color="primary" onClick={() => handleClose?.(ModalsActions.CLOSE)}>
                    {t(l.Close)}
                </Button>
            }
        >
            {isVisible && (
                <BonusInfoModalBody
                    id={bonusData?.Id}
                    title={bonusData?.Name}
                    description={bonusData?.Description}
                    type={bonusData?.Type}
                    startDate={bonusData?.StartDate}
                    endDate={bonusData?.EndDate}
                    bonusDetails={bonusData?.BonusDetails ?? []}
                    wallet={bonusData?.BalanceType}
                    isLoading={isLoading}
                />
            )}
        </Modal>
    );
};

export default memo(VersionHistoryProviderHOC(BonusModal));
